import {
  useDate,
  useDestination,
  useHistory,
  usePassengerCount,
  useSubmit,
  useValidation
} from "@website/accommodation-ui/villa-search/villa-search-panel/hooks";
import {
  PassengerCountInitialValuesType,
  VillaSearchPanelPropertiesType
} from "@website/accommodation-ui/villa-search/villa-search-panel/villa-search-panel.types";
import { VillaSearchPassengerCount } from "@website/accommodation-ui/villa-search/villa-search-passenger-count";
import { MaterialIcon } from "@website/icons/material";
import { AutoComplete } from "@website/ui/auto-complete";
import { Button } from "@website/ui/button";
import { Datepicker } from "@website/ui/datepicker";
import { deepCompare } from "@website/utils/deep-compare";
import { FC, memo } from "react";

const Component: FC<VillaSearchPanelPropertiesType> = (properties) => {
  const {
    size,
    searchType,
    initialCityData,
    onClose,
    passengerCountInitialValues,
    datePickerInitialValues,
    handleSearchSubmit,
    searchSuggestion,
    searchAutocompleteFetcher
  } = properties;
  const {
    passengerCount,
    onSubmitPassengerCount,
    passengerCountList,
    passengerCountTitle,
    passengerCountResult,
    passengerCountValidation
  } = usePassengerCount({
    initialValues:
      passengerCountInitialValues as PassengerCountInitialValuesType,
    searchType
  });

  const {
    selectedDestinationItem,
    selectedDestinationText,
    destinationQuery,
    onChangeDestination,
    onChangeDestinationQuery,
    onSelectDestination,
    destinationLoading,
    destinationListNormalized,
    DestinationNoResult
  } = useDestination({
    cityTitleInitialValues: initialCityData?.title,
    searchSuggestion,
    searchAutocompleteFetcher
  });
  const { date, onChangeDate } = useDate(datePickerInitialValues);
  const { historyListNormalized, addToHistory, onSelectHistory } =
    useHistory(onChangeDestination);
  const { destinationError, dateError, submitValidationCheck } = useValidation({
    acceptEmpty: true,
    selectedDestinationItem,
    date,
    initialDestinationItem: initialCityData,
    searchType
  });
  const { onSubmitClick, submitLoading } = useSubmit(
    selectedDestinationItem,
    date,
    passengerCount,
    addToHistory,
    submitValidationCheck,
    handleSearchSubmit,
    initialCityData
  );

  return (
    <div
      className={`${
        searchType === "villa" ? "gap-8" : "gap-6"
      } relative mb-4 flex h-full flex-col lg:mb-0 lg:h-auto`}
    >
      <h3 className="text-headline-6 lg:hidden">
        {searchType === "villa" ? "تعیین بازه اقامت و مسافران" : "اقامتگاه"}
      </h3>

      {(searchType === "city" || searchType === "category") && (
        <div className="flex h-8 flex-row justify-between gap-2">
          <VillaSearchPassengerCount
            list={passengerCountList}
            onSubmit={onSubmitPassengerCount}
            result={passengerCountResult}
            searchType={searchType}
            size="dense"
            title={passengerCountTitle}
            validation={passengerCountValidation}
          />
        </div>
      )}

      {searchType === "home" && (
        <div className="flex h-8 flex-row justify-between gap-2">
          <VillaSearchPassengerCount
            list={passengerCountList}
            onSubmit={onSubmitPassengerCount}
            result={passengerCountResult}
            searchType={searchType}
            title="تعداد مسافر"
            validation={passengerCountValidation}
          />
        </div>
      )}

      <div
        className={`flex flex-col gap-4 xl:flex-row ${
          searchType === "villa" ? "w-full" : ""
        }`}
      >
        <div
          className={`flex flex-col gap-4 lg:flex-row ${
            searchType === "city" ? "flex-1" : "w-full lg:items-center"
          }`}
        >
          {searchType !== "villa" && (
            <div
              className={`flex-[3] ${
                searchType === "category"
                  ? "pointer-events-none opacity-60"
                  : ""
              }`}
            >
              <AutoComplete
                error={destinationError}
                fullWidth
                parts={[
                  {
                    label: "شهر یا اقامتگاه مقصد",
                    list: DestinationNoResult ? [] : destinationListNormalized,
                    historyList:
                      !DestinationNoResult && !destinationLoading
                        ? historyListNormalized
                        : [],
                    historyTitle: "جستجو های اخیر",
                    text: selectedDestinationText || "",
                    onSelect: onSelectDestination,
                    onSelectHistory,
                    onChangeQuery: onChangeDestinationQuery,
                    query: destinationQuery,
                    loading: destinationLoading,
                    NoResult: DestinationNoResult
                  }
                ]}
                size={size}
              />
            </div>
          )}
          <div
            className={`${
              searchType === "villa" ? "flex-auto lg:max-w-[312px]" : "flex-[2]"
            }`}
          >
            <Datepicker
              acceptEmpty
              error={dateError.length > 0}
              fromTitle="تاریخ ورود"
              fullWidth
              gapHorizontal={searchType === "villa" ? "0px" : "-16px"}
              gapVertical={searchType === "villa" ? "42px" : "-16px"}
              helperText={dateError}
              mode="range"
              onChange={onChangeDate}
              position={searchType === "villa" ? "start" : "end"}
              size={searchType === "villa" ? "dense" : "default"}
              toTitle="تاریخ خروج"
              value={date ?? []}
            />
          </div>
          {/* searchType === "villa" && (
            <div className={"flex-auto lg:max-w-[288px]"}>
              <VillaSearchPassengerCount
                list={passengerCountList}
                onSubmit={onSubmitPassengerCount}
                result={passengerCountResult}
                searchType={searchType}
                size={searchType === "villa" ? "dense" : "default"}
                title={passengerCountTitle}
                validation={passengerCountValidation}
              />
            </div>
          ) */}
          <div
            className={`${
              searchType === "home" ? "static" : "absolute"
            } bottom-0 w-full lg:relative lg:w-[134px]`}
          >
            <Button
              color={searchType === "villa" ? "secondary" : "primary"}
              fullWidth
              loading={submitLoading}
              onClick={onSubmitClick}
              size={searchType === "villa" ? "medium" : "x-large"}
            >
              بروزرسانی جستجو
            </Button>
          </div>
          <div
            className={`absolute left-0 top-0 mr-auto hidden text-center ltr:ml-auto ${
              searchType === "villa" ? "xl:mt-0" : ""
            } ${searchType === "home" ? "lg:hidden" : "lg:block"}`}
          >
            <Button
              StartIcon={<MaterialIcon name="close" />}
              color="neutral"
              onClick={onClose}
              size="small"
              variant="text"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const VillaSearchPanel = memo(Component, deepCompare);
